import { Bridge } from '@k4connect/capacitor-plugin'
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import _ from 'lodash';
import { v5 as uuidv5 } from 'uuid';
import auth from '@/api/auth';
import env from '@/api/env';
import router from '@/router';

import { set, get } from "@/utils/vuex";

const state = () => ({
	native: false,
	deviceInfo: {},
	uniqueIdentifier: null,
	inExternal: false,
	inVideo: false,
	frameLink: null
});

const channels = {
	dev: 'Development',
	staging: 'Staging',
	production: 'Production'
}


const getters = {
	
	isK4App: get('native'),
	credentials: get('credentials'),
	getFrameLink: get('frameLink'),
	isAndroid: (state) => {
		return state.deviceInfo.platform === 'android';
	},
	isIos: (state) => {
		return state.deviceInfo.platform === 'ios';
	},
	isWeb: (state) => {
		return state.deviceInfo.platform === 'web';
	},
	info: get('deviceInfo'),

	deviceId: (state, getters, rootState, rootGetters) => {
		rootGetters['settings/value']('deviceId');
	},

	platform: (state) => {
		return state.deviceInfo.platform;
	},

	name: (state) => {
		const { deviceInfo, uniqueIdentifier } = state;
		return `${ (deviceInfo.name) ? deviceInfo.name + '_' : '' }${deviceInfo.platform}_${uniqueIdentifier}`;
	},

};

const mutations = {
	setNative: set("native"),
	setDeviceInfo: set("deviceInfo"),
	setUniqueIdentifier: set("uniqueIdentifier"),
	setCredentials: set("credentials"),
	setInExternal: set("inExternal"),
	setInVideo: set("inVideo"),
	setFrameLink: set("frameLink")
};

const actions = {
	init: async ({commit, rootGetters, dispatch}) => {
		
		

		const native = Capacitor.isNativePlatform();
		commit('setNative', native);

		const { uuid } = await Device.getId();
		commit('setUniqueIdentifier', uuid);

		const deviceInfo = await Device.getInfo();
		commit('setDeviceInfo', deviceInfo);


		const deviceId = rootGetters['settings/value']('deviceId');

		if(_.isEmpty(deviceId) || deviceId === 'undefined')
		{
			const id = await auth.getDevice();
			await dispatch('settings/save', { name: 'deviceId', value: id }, { root: true });
		}

		if(native)
		{

			const jwt = await auth.getCredentials();
			
			const sso = await env.sso();
			const environment = await env.name();

			//init bridge
			await Bridge.initialize({ jwt, sso, environment });

			try {
				// Assert the env channel, but don't clobber a channel a console operator
				// force-assigned for targeted delivery — any non-env channel is left alone.
				const desired = channels[environment];
				const knownEnvChannels = Object.values(channels);
				const { channel: current } = await LiveUpdate.getChannel();

				if(current === null || (knownEnvChannels.includes(current) && current !== desired))
				{
					await LiveUpdate.setChannel({ channel: desired });
				}

				// Tag this device with the user's GUID so the Capawesome console can
				// trace a device back to a user for targeted live updates.
				const { guid } = auth.parseJwt(jwt);
				if(guid)
				{
					await LiveUpdate.setCustomId({ customId: guid });
				}
			} catch(e) {
				console.error('Error configuring LiveUpdate', e);
			}

		}

	},

	checkForDeepLink: async({state}) => {

		if(!state.native)
		{
			return;
		}

		return new Promise((resolve) => {

			console.log('Checking for deeplink...');

			let attempts = 7;

			const timer = setInterval(async () => {
				
				try {
					
					const { url } = await Bridge.getDeeplink();
					
					if(url)
					{
						clearInterval(timer);

						const link = new URL(url);
						const action = link.searchParams.get('action');
						const redirect = link.searchParams.get('redirect');
						
						if(action === 'migration' && redirect)
						{
							console.log('Migrate deeplink found...', redirect);
							window.location.href = env.login();
						}

						resolve();
					}
					

				} catch(e) {
					//no link, invalid link
				}

				attempts--;

				if(attempts <= 0)
				{
					clearInterval(timer);
					resolve();
				}
				
			}, 1000);

		});
		
	},

	saveCredentials: async ({commit, state}, credentials) => {
		
		if(state.detected)
		{
			await Bridge.saveKeychain(credentials);
			commit('setCredentials', credentials);
		}
		
	},

	call: async ({getters, commit}, payload) => {

		if(getters.isK4App)
		{
			
			await Bridge.showVideo({payload});	
			commit('setInVideo', true);
		}
	},

	

	open: async ({getters, commit, dispatch}, url) => {
		commit('setInExternal', true);


		const link = new URL(url);
		const { searchParams, protocol } = link;

		//populate jwt
		if(searchParams.has('jwt'))
		{
			searchParams.set('jwt', auth.jwt);
		}
	
		if(protocol && protocol.indexOf('http') !== 0)
		{
			//alternate universal link
			if(searchParams.has('universal'))
			{
				const universal = new URL(searchParams.get('universal'));

				//populate jwt
				if(universal.searchParams.has('jwt'))
				{
					universal.searchParams.set('jwt', auth.jwt);
				}

				//launch out of app if universal link is passed and platform is set to use universal
				if(searchParams.get('ios') == 'universal' && getters.isIos)
				{
					return window.open(universal.toString(), '_blank');
				}

				if(searchParams.get('android') == 'universal' && getters.isAndroid)
				{
					return window.open(universal.toString(), '_blank');
				}

				if(searchParams.get('web') == 'universal' && getters.isWeb)
				{
					return window.open(universal.toString(), '_blank');
				}
					
			}

			//universal was not used, cleanup standard deeplink
			searchParams.delete('universal');
			searchParams.delete('ios');
			searchParams.delete('android');
			searchParams.delete('web');
			
			if(getters.isK4App)
			{
				await dispatch('openDeeplink', link.toString());
			} else {
				window.open(link.toString(), '_blank');
			}
			
		} else if(searchParams.has('frame')){

			searchParams.delete('frame');
			commit('setFrameLink', link.toString());
			router.push('/frame');

		} else if(getters.isK4App) {
			await Bridge.showExternal({
				url: link.toString()
			});
		} else {
			window.open(link.toString(), '_blank');
		}
	
	},

	resume: async ({commit, state}) => {

		//clear inExternal or inVideo, and return true if neither were true before resume
		const fromBackground = !state.inExternal && !state.inVideo;

		console.log('fromBackground: ' + fromBackground + ' inExternal: ' + state.inExternal + ' inVideo: ' + state.inVideo);

		commit('setInExternal', false);
		commit('setInVideo', false);

		return fromBackground;

	},

	updateDeviceNameForWeb: async({commit}, userId) => {
		const deviceInfo = await Device.getInfo();

		if(deviceInfo.platform === 'web') {
			const uuid = uuidv5('web', userId);
			commit('setUniqueIdentifier', uuid);
		}
	},

	clearMobileJWT: async({state}) => {

		const { native }= state;

		if(native) {
			const sso = await env.sso();
			const environment = await env.name();
			const jwt = '';
			await Bridge.initialize({ jwt, sso, environment });

			// Clear the user mapping on logout so a shared/handoff device doesn't keep
			// the previous user's identity until the next login re-runs init.
			try {
				await LiveUpdate.setCustomId({ customId: null });
			} catch(e) {
				console.error('Error clearing LiveUpdate customId', e);
			}
		}

	},

	openCalendar: async ({getters, commit}, url) => {
		commit('setInExternal', true);

		if(getters.isK4App) {
			await Bridge.showExternal({ url });
		} else {
			window.open(url, '_blank');
		}
	},

	openDeeplink: async(ctx, link) => {
		await Bridge.openDeeplink({ link });
	},

	clearDeeplink: async() => {
		await Bridge.clearDeeplink({});
	},
	
};


export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
